import React, { lazy } from 'react';
import { Route } from 'react-router-dom';
import { OnboardingWizardGuard } from 'guard/OnboardingWizardGuard';
import { TicketingRoutesGuard } from 'guard/TicketingRouteGuard';
import { UnleashFeatureGuard } from 'guard/UnleashFeatureGuard';
import { SuspenseOutlet } from 'utils/routing/SuspenseOutlet';
import { FEATURE_TICKETING_ONBOARDING_WIZARD } from 'utils/flags/constants';
import { TicketRedirectHandler } from 'containers/Ticketing/RequesterPortal/TicketRedirectHandler';
import RouteAnalytics from './RouteAnalytics';

const TicketingOnboarding = lazy(
  () => import('containers/Ticketing/Onboarding')
);
const OnboardingWizard = lazy(
  () => import('pages/app/ticketing/onboarding-wizard')
);
const ResetOnboardingWizard = lazy(
  () => import('pages/app/ticketing/onboarding-wizard/ResetOnboardingWizard')
);
const MatterDetailSwitcher = lazy(
  () =>
    import(
      'containers/Ticketing/Boards/Tickets/MatterDetail/MatterDetailSwitcher'
    )
);
const FieldsTabContent = lazy(
  () =>
    import(
      'containers/Ticketing/Boards/Tickets/MatterDetail/tabs/FieldsTabContent'
    )
);
const ConversationsTabContent = lazy(
  () =>
    import(
      'containers/Ticketing/Boards/Tickets/MatterDetail/tabs/ConversationsTabContent'
    )
);
const FilesTabContent = lazy(
  () =>
    import(
      'containers/Ticketing/Boards/Tickets/MatterDetail/tabs/FilesTabContent'
    )
);
const TasksTabContent = lazy(
  () =>
    import(
      'containers/Ticketing/Boards/Tickets/MatterDetail/tabs/TasksTabContent'
    )
);
const CalendarTabContent = lazy(
  () =>
    import(
      'containers/Ticketing/Boards/Tickets/MatterDetail/tabs/CalendarTabContent'
    )
);
const HistoryTabContent = lazy(
  () =>
    import(
      'containers/Ticketing/Boards/Tickets/MatterDetail/tabs/HistoryTabContent'
    )
);
const ThreadsRedirect = lazy(
  () =>
    import('containers/Ticketing/Boards/Tickets/MatterDetail/ThreadsRedirect')
);
const TicketParticipantView = lazy(
  () =>
    import(
      'containers/Ticketing/Boards/Tickets/Ticket/ConversationParticipant/TicketParticipantView'
    )
);
const TicketingAllBoards = lazy(
  () => import('containers/Ticketing/Boards/AllBoards')
);
const TicketingBoards = lazy(
  () => import('containers/Ticketing/Boards/Boards')
);
const TicketingTickets = lazy(
  () => import('containers/Ticketing/Boards/Tickets/Tickets')
);
const TicketingBoardSettings = lazy(
  () => import('containers/Ticketing/Boards/Admin/Settings')
);
const TicketingBoardMembers = lazy(
  () => import('containers/Ticketing/Boards/Admin/Permissions')
);
const TicketingBoardNotifications = lazy(
  () => import('containers/Ticketing/Boards/Admin/Notifications')
);
const TicketingBoardIntegrations = lazy(
  () => import('containers/Ticketing/Boards/Admin/Integrations')
);
const TicketingVerifyEmail = lazy(
  () => import('containers/Ticketing/Boards/VerifyEmail')
);
const TicketingDashboard = lazy(
  () => import('containers/Ticketing/Dashboard/Dashboard')
);
const TicketingRequesterPortal = lazy(
  () => import('containers/Ticketing/RequesterPortal/Tickets/List')
);
const TicketingRequesterPortalDetails = lazy(
  () =>
    import(
      'containers/Ticketing/RequesterPortal/ConversationRequester/RequesterMatterDetailSwitcher'
    )
);
const TicketingAuditPortal = lazy(
  () => import('containers/Ticketing/AuditPortal/Tickets/List')
);
const TicketingAuditPortalDetails = lazy(
  () => import('containers/Ticketing/AuditPortal/TicketAuditView')
);
const TicketingBoardsNotFound = lazy(
  () => import('containers/Ticketing/Boards/NotFound')
);

export const matterRoutes = () => (
  <>
    <Route path="ticketing/redirect" element={<TicketRedirectHandler />} />
    <Route path="ticketing/portal" element={<TicketingRequesterPortal />} />
    <Route
      path="ticketing/portal/:ticketId"
      element={<TicketingRequesterPortalDetails />}
    />
    <Route
      path="ticketing/portal/:ticketId/threads/:threadId"
      element={<TicketingRequesterPortalDetails />}
    />

    <Route path="ticketing/audit/portal" element={<TicketingAuditPortal />} />
    <Route
      path="ticketing/audit/portal/:ticketId"
      element={<TicketingAuditPortalDetails />}
    />
    <Route
      path="ticketing/audit/portal/:ticketId/threads/:threadId"
      element={<TicketingAuditPortalDetails />}
    />

    <Route
      path="ticketing"
      element={
        <TicketingRoutesGuard>
          <TicketingBoards>
            <SuspenseOutlet />
          </TicketingBoards>
        </TicketingRoutesGuard>
      }
    >
      <Route
        path="onboarding"
        element={
          <RouteAnalytics pageName="Onboarding">
            <TicketingOnboarding />
          </RouteAnalytics>
        }
      />
      <Route
        path="onboarding-wizard"
        element={
          <UnleashFeatureGuard
            featureFlag={FEATURE_TICKETING_ONBOARDING_WIZARD}
            redirectPath="/app"
          >
            <OnboardingWizardGuard>
              <RouteAnalytics pageName="Onboarding Wizard">
                <OnboardingWizard />
              </RouteAnalytics>
            </OnboardingWizardGuard>
          </UnleashFeatureGuard>
        }
      />
      <Route
        path="onboarding-wizard/reset"
        element={
          <UnleashFeatureGuard
            featureFlag={FEATURE_TICKETING_ONBOARDING_WIZARD}
            redirectPath="/app"
          >
            <OnboardingWizardGuard>
              <RouteAnalytics pageName="Onboarding Wizard Reset">
                <ResetOnboardingWizard />
              </RouteAnalytics>
            </OnboardingWizardGuard>
          </UnleashFeatureGuard>
        }
      />
      <Route
        path="tickets/:ticketId/participant"
        element={
          <RouteAnalytics pageName="Ticket Details Unauthorized Page">
            <TicketParticipantView />
          </RouteAnalytics>
        }
      />
      <Route
        path="tickets/:ticketId/threads/:threadId/participant"
        element={
          <RouteAnalytics pageName="Ticket Details Unauthorized Page">
            <TicketParticipantView />
          </RouteAnalytics>
        }
      />
      {/* Matter Detail page with sub-navigation.
            Flag ON: MatterDetailPage with nested tab routes.
            Flag OFF: old TicketDetailsPage (child routes ignored). */}
      <Route
        path="tickets/:ticketId"
        element={
          <RouteAnalytics pageName="Ticket Details Page">
            <MatterDetailSwitcher />
          </RouteAnalytics>
        }
      >
        <Route index element={<FieldsTabContent />} />
        <Route path="fields" element={<FieldsTabContent />} />
        <Route path="conversations" element={<ConversationsTabContent />} />
        <Route
          path="conversations/:threadId"
          element={<ConversationsTabContent />}
        />
        <Route path="files" element={<FilesTabContent />} />
        <Route path="tasks" element={<TasksTabContent />} />
        <Route path="calendar" element={<CalendarTabContent />} />
        <Route path="history" element={<HistoryTabContent />} />
        {/* Backward compat: old thread URLs redirect to conversations */}
        <Route path="threads/:threadId" element={<ThreadsRedirect />} />
      </Route>
      <Route
        path="boards/all"
        element={
          <RouteAnalytics pageName="Boards List Page">
            <TicketingAllBoards />
          </RouteAnalytics>
        }
      />
      <Route path="boards/:id">
        <Route
          path="admin/settings"
          element={
            <RouteAnalytics pageName="Board Settings">
              <TicketingBoardSettings />
            </RouteAnalytics>
          }
        />
        <Route
          path="admin/members"
          element={
            <RouteAnalytics pageName="Board Members">
              <TicketingBoardMembers />
            </RouteAnalytics>
          }
        />
        <Route
          path="admin/notifications"
          element={
            <RouteAnalytics pageName="Board Notifications">
              <TicketingBoardNotifications />
            </RouteAnalytics>
          }
        />
        <Route
          path="admin/integrations"
          element={
            <RouteAnalytics pageName="Integrations">
              <TicketingBoardIntegrations />
            </RouteAnalytics>
          }
        />
        <Route
          path="views/:viewId/tickets"
          element={
            <RouteAnalytics pageName="Tickets">
              <TicketingTickets />
            </RouteAnalytics>
          }
        />
        <Route
          path="verify-email/:token"
          element={
            <RouteAnalytics pageName="Verify Email Ticketing">
              <TicketingVerifyEmail />
            </RouteAnalytics>
          }
        />
      </Route>
      <Route
        path="dashboards"
        element={
          <RouteAnalytics pageName="Dashboard">
            <TicketingDashboard />
          </RouteAnalytics>
        }
      />
      <Route
        path="not-found"
        element={
          <RouteAnalytics pageName="NotFound">
            <TicketingBoardsNotFound />
          </RouteAnalytics>
        }
      />
    </Route>
  </>
);
